import type { ApiEnvelope } from '../auth/types';
import { syncedDataUrl } from '../config/api';
import { postJson, RequestFailure } from './httpClient';

export type UserDataErrorKind = 'network' | 'server' | 'api' | 'parse' | 'unknown';

export class UserDataRequestError extends Error {
  readonly kind: UserDataErrorKind;

  constructor(kind: UserDataErrorKind, message: string) {
    super(message);
    this.name = 'UserDataRequestError';
    this.kind = kind;
  }
}

interface StationaryPointDoc {
  name?: string;
  geographic_address?: string;
  is_favourite?: boolean;
  launches?: number | string;
}

interface PlaylistDoc {
  name?: string;
  points_count?: number | string;
  favourite?: boolean;
  label_color?: string;
  playlist_points?: unknown[];
}

interface ItineraryDoc {
  itinerary?: Array<{ itenaryName?: string; noOfLaunches?: number | string }>;
  sub_itinerary?: unknown[];
}

interface SyncedDataPayload {
  user?: Record<string, unknown>;
  stationarypoints?: StationaryPointDoc[];
  playlists?: PlaylistDoc[];
  itineraries?: ItineraryDoc[];
}

export interface SyncHighlight {
  name: string;
  detail?: string;
  accent?: string;
}

export interface UserSyncSummary {
  stationaryPointsCount: number;
  favoritePointsCount: number;
  playlistsCount: number;
  playlistPointsCount: number;
  favoritePlaylistsCount: number;
  itinerariesCount: number;
  subItinerariesCount: number;
  totalLaunches: number;
  recentPoints: SyncHighlight[];
  recentPlaylists: SyncHighlight[];
  hasAnyData: boolean;
}

function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toUserDataError(error: unknown): UserDataRequestError {
  if (error instanceof RequestFailure) {
    const kind =
      error.kind === 'server'
        ? 'server'
        : error.kind === 'parse'
          ? 'parse'
          : 'network';
    return new UserDataRequestError(kind, error.message);
  }
  if (error instanceof UserDataRequestError) {
    return error;
  }
  if (error instanceof Error) {
    return new UserDataRequestError('unknown', error.message);
  }
  return new UserDataRequestError(
    'unknown',
    'Something went wrong while loading your data.'
  );
}

function buildSummary(payload: SyncedDataPayload): UserSyncSummary {
  const points = payload.stationarypoints ?? [];
  const playlists = payload.playlists ?? [];
  const itineraryDocs = payload.itineraries ?? [];

  const favoritePointsCount = points.filter((p) => Boolean(p.is_favourite)).length;
  const favoritePlaylistsCount = playlists.filter((p) => Boolean(p.favourite)).length;

  const playlistPointsCount = playlists.reduce((sum, playlist) => {
    const fromField = toNumber(playlist.points_count);
    const fromArray = Array.isArray(playlist.playlist_points)
      ? playlist.playlist_points.length
      : 0;
    return sum + (fromField || fromArray);
  }, 0);

  let itinerariesCount = 0;
  let subItinerariesCount = 0;
  let itineraryLaunches = 0;

  for (const doc of itineraryDocs) {
    const routes = doc.itinerary ?? [];
    const subs = doc.sub_itinerary ?? [];
    itinerariesCount += routes.length;
    subItinerariesCount += subs.length;
    itineraryLaunches += routes.reduce(
      (sum, route) => sum + toNumber(route.noOfLaunches),
      0
    );
  }

  const pointLaunches = points.reduce(
    (sum, point) => sum + toNumber(point.launches),
    0
  );

  const recentPoints: SyncHighlight[] = points.slice(0, 3).map((point) => ({
    name: point.name?.trim() || 'Unnamed location',
    detail: point.geographic_address?.trim() || undefined,
  }));

  const recentPlaylists: SyncHighlight[] = playlists.slice(0, 3).map((playlist) => {
    const count =
      toNumber(playlist.points_count) ||
      (Array.isArray(playlist.playlist_points) ? playlist.playlist_points.length : 0);
    return {
      name: playlist.name?.trim() || 'Unnamed playlist',
      detail: count === 1 ? '1 point' : `${count} points`,
      accent: playlist.label_color?.trim() || undefined,
    };
  });

  const hasAnyData =
    points.length > 0 ||
    playlists.length > 0 ||
    itinerariesCount > 0 ||
    subItinerariesCount > 0;

  return {
    stationaryPointsCount: points.length,
    favoritePointsCount,
    playlistsCount: playlists.length,
    playlistPointsCount,
    favoritePlaylistsCount,
    itinerariesCount,
    subItinerariesCount,
    totalLaunches: pointLaunches + itineraryLaunches,
    recentPoints,
    recentPlaylists,
    hasAnyData,
  };
}

export async function fetchUserSyncSummary(
  userId: string,
  signal?: AbortSignal
): Promise<UserSyncSummary> {
  if (!userId.trim()) {
    throw new UserDataRequestError(
      'api',
      'Your account ID is missing. Try signing out and back in.'
    );
  }

  try {
    const envelope = (await postJson(
      syncedDataUrl(),
      { user_id: userId },
      signal
    )) as ApiEnvelope<SyncedDataPayload>;

    if (envelope.result === 0) {
      throw new UserDataRequestError(
        'api',
        envelope.message || 'Unable to load your synced data.'
      );
    }

    if (!envelope.response) {
      throw new UserDataRequestError(
        'api',
        'Synced data was missing from the server response.'
      );
    }

    return buildSummary(envelope.response);
  } catch (error) {
    if (error instanceof UserDataRequestError) {
      throw error;
    }
    throw toUserDataError(error);
  }
}

export function userDataErrorTitle(kind: UserDataErrorKind): string {
  switch (kind) {
    case 'network':
      return 'No connection';
    case 'server':
      return 'Server unavailable';
    case 'parse':
      return 'Unexpected response';
    case 'api':
      return 'Could not load data';
    default:
      return 'Something went wrong';
  }
}
